import { faX } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Modal } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useMinWidthQuery } from '../../../hooks/useMediaQuery';
import LoadingSpinner from '../../../components/loadingSpinner/LoadingSpinner';
import { OutletIProps } from '../../../components/sitebase/Base.types';
import DynamicField from '../compose/DynamicField';
import { EFAS_FROM, buildNotificationSubject, fetchAlertTypeCC, fetchPartnerEmails, sendNotificationEmail, sendTestEmailToSelf } from '../compose/notificationEmailUtil';

import '../compose/ComposeApp.css';
import type { CartNotificationData, EFASNotification } from '../notifications/Notification.types';

/**
 * Resends the email of a notification that has ALREADY been sent - i.e. the
 * SentNotification and its AlertPoints exist, but the email did not go out
 * (typically an upstream SMTP failure).
 *
 * It only replays the email step: the stored, fully rendered body
 * (`data_detailed`) is sent as-is and nothing is written to /api/notifications/.
 * The recipients and the subject can be reviewed before sending, and the subject
 * is rebuilt with the same logic compose uses because it is not stored.
 *
 * Rendered only while open - mounting it opens the modal, `onClose` closes it.
 */
export default function ResendNotificationModal(props: {
    notification: EFASNotification,
    onClose: () => void,
}) {
    const context = useOutletContext<OutletIProps>();
    const isSmallWidth = useMinWidthQuery();

    const summary: CartNotificationData = JSON.parse(props.notification.data_summary);

    const [body, setBody] = useState<string>(props.notification.data_detailed || "");
    const [subject, setSubject] = useState<string>(buildNotificationSubject(props.notification.notification_type, summary.points));
    const [sending, setSending] = useState<boolean>(false);

    const [originalTo, setOriginalTo] = useState<string[]>([]);
    const [activeTo, setActiveTo] = useState<string[]>([]);
    const [originalCC, setOriginalCC] = useState<string[]>([]);
    const [activeCC, setActiveCC] = useState<string[]>([]);

    useEffect(() => {
        // the list endpoint already returns data_detailed, but re-fetch the single
        // notification so the resend always mails the currently stored body
        if (!body) {
            fetch(`/api/notifications/sent/?format=json&notification_id=${props.notification.notification_id}`)
                .then((response) => response.json())
                .then((data: EFASNotification[]) => setBody(data[0]?.data_detailed || ""))
                .catch((error) => {
                    console.error("Error fetching the notification body:", error);
                });
        }

        const partnerIds = Array.from(new Set(props.notification.alert_points_expanded.flatMap((point) => point.partners || [])));
        const ids: (string | number)[] = partnerIds.length > 0 ? partnerIds : (summary.points[0]?.INST_ID || "").split(",").filter((id) => id.length > 0);

        fetchPartnerEmails(ids)
            .then((allEmails) => {
                setOriginalTo(allEmails);
                setActiveTo(allEmails);
            })
            .catch((error) => {
                console.error("Error fetching data:", error);
            });

        fetchAlertTypeCC(props.notification.notification_type)
            .then((ccEmails) => {
                setOriginalCC(ccEmails);
                setActiveCC(ccEmails);
            })
            .catch((error) => {
                console.error("Error fetching data:", error);
            });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only effect: `props.notification`/`summary.points`/`body` are read once when the modal mounts; `summary` is re-parsed every render so adding it would re-fetch and re-run on every render
    }, []);

    const removeTo = useCallback((entry: string) => setActiveTo((prev) => prev.filter((e) => e !== entry)), []);
    const addTo = useCallback((entry: string) => setActiveTo((prev) => [...prev, entry]), []);
    const removeCC = useCallback((entry: string) => setActiveCC((prev) => prev.filter((e) => e !== entry)), []);
    const addCC = useCallback((entry: string) => setActiveCC((prev) => [...prev, entry]), []);

    const sendTest = async () => {
        setSending(true);
        await sendTestEmailToSelf(subject, body, context.user.email);
        setSending(false);
    };

    const resend = async () => {
        setSending(true);
        const response = await sendNotificationEmail({
            from: EFAS_FROM,
            to: activeTo.join(","),
            cc: activeCC.join(","),
            subject: subject,
            body: body,
        });
        const json = await response.json();
        setSending(false);

        if (response.ok) {
            toast.success(`Notification email resent - ${json.message}`);
            props.onClose();
        } else {
            toast.error("Encountered an error while resending the notification email");
            // TODO this should report the error to the backend somehow!
            console.error('Error resending the notification email:', json);
        }
    };

    const country = props.notification.alert_points_expanded[0]?.country;
    const river = props.notification.alert_points_expanded.map((point) => point.river_name).join(", ");

    return <Modal open={true} onClose={props.onClose} aria-labelledby='resend-modal-title'>
        <Box sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "background.paper",
            boxShadow: 24,
            borderRadius: "5px",
            p: 2.5,
            width: isSmallWidth ? "95vw" : "60vw",
            maxHeight: "86vh",
            overflowY: "auto"
        }}>
            <div className='d-flex flex-row'>
                <h5 id='resend-modal-title'>Resend notification email</h5>
                <div className='ms-auto btn btn-outline-dark my-auto' role='button' aria-label='Close modal'
                    onClick={props.onClose}><FontAwesomeIcon icon={faX} /></div>
            </div>
            <div className='fs-8 mb-2'>
                No new notification is created - only the email of the existing notification
                for <strong>{country}</strong>{river && <> - River: <strong>{river}</strong></>} is sent again.
            </div>

            <DynamicField title='To'
                original_entries={originalTo}
                active_entries={activeTo}
                removeEntry={removeTo}
                addEntry={addTo}
                removeAll={() => setActiveTo([])}
            />

            <DynamicField title='CC'
                original_entries={originalCC}
                active_entries={activeCC}
                removeEntry={removeCC}
                addEntry={addCC}
                removeAll={() => setActiveCC([])}
            />

            <div className='form-floating mb-3'>
                <input type='text' className='form-control' id='resend-subject' placeholder='Subject'
                    value={subject} onChange={(e) => setSubject(e.target.value)} />
                <label htmlFor='resend-subject'>Subject</label>
            </div>

            <div className='fs-8'>Email body (as stored when the notification was created)</div>
            <div className='border rounded p-2 mb-3 overflow-auto' style={{ maxHeight: "40vh" }} data-testid='resend-body-preview'>
                {body ? <div dangerouslySetInnerHTML={{ __html: body }} /> : <LoadingSpinner className='mx-auto text-center' />}
            </div>

            <div className='d-flex flex-row'>
                <button id='resend-send-self' className='btn btn-warning border-dark m-1 fs-8 my-auto'
                    disabled={sending || !body}
                    onClick={sendTest}>Send Only To Myself</button>
                <button id='resend-send' className='btn btn-danger border-dark m-1 fs-8 my-auto'
                    disabled={sending || !body || activeTo.length === 0}
                    onClick={() => {
                        if (confirm(`Do you want to resend the notification email for\n\nCountry: ${country}\nRiver: ${river}?`)) resend()
                    }}>RESEND NOTIFICATION</button>
                <button id='resend-cancel' className='btn btn-warning border-dark m-1 fs-8 my-auto'
                    onClick={props.onClose}>Cancel</button>
            </div>
        </Box>
    </Modal>;
}
